// The token-route and credential-param rules live in @atriam/observability,
// which applies the same redaction to OTel spans and logs; PostHog's event
// shape is the only part specific to this module.
import {
  isTokenRoute,
  redactTokenUrl,
} from "@atriam/observability/redact-token-urls";

export { isTokenRoute, redactTokenUrl };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function redactOwnStrings(bag: Record<string, unknown>): void {
  for (const key of Object.keys(bag)) {
    const value = bag[key];
    if (typeof value === "string") bag[key] = redactTokenUrl(value);
  }
}

/**
 * Redacts every string value in `bag`, plus string values nested one level
 * inside a plain-object value (e.g. `$initial_person_info: { u, r }`).
 * Not just a fixed key list — posthog-js isn't limited to
 * `$current_url`/`$pathname`/etc.: `SessionPropsManager.getSessionProps()`
 * mints `$session_entry_<anything>` keys, and `set_initial_person_info()`
 * stores a raw `{ u: <href>, r: <referrer> }` object in `$set_once`. Arrays
 * (e.g. `$exception_list`) are left alone — `redactTokenUrl` is a no-op on
 * any string that isn't a token-route URL, so redacting unconditionally
 * doesn't corrupt unrelated values — except autocapture's deprecated
 * `$elements`, still sent unless the project's remote config switches to
 * elementsChainAsString: its first entry's `attr__href` is the clicked
 * link, the same value $elements_chain and $external_click_url carry.
 */
function redactStringsInPlace(bag: Record<string, unknown>): void {
  for (const key of Object.keys(bag)) {
    const value = bag[key];
    if (typeof value === "string") {
      bag[key] = redactTokenUrl(value);
    } else if (isPlainObject(value)) {
      redactOwnStrings(value);
    } else if (key === "$elements" && Array.isArray(value)) {
      for (const element of value) {
        if (isPlainObject(element)) redactOwnStrings(element);
      }
    }
  }
}

export function redactTokenProperties<
  T extends {
    properties?: Record<string, unknown>;
    $set?: Record<string, unknown>;
    $set_once?: Record<string, unknown>;
  },
>(event: T): T {
  for (const bag of [event.properties, event.$set, event.$set_once]) {
    if (!bag) continue;
    redactStringsInPlace(bag);
  }
  return event;
}
